<template>
  <div class="">
      <sonicu-header>Config Parameters</sonicu-header>
      
      <banner-success v-model:is-open="showSuccessBanner" :auto-hide="5000">
        <template #title>Success</template>
        <template #content>
          <p>{{ successMessage }}</p>
        </template>
      </banner-success>

      <slide-over-panel
          v-model:show="$data._show"
          @update:show="updateShow($data._show)"
      >
          <template #title>
              <span class="text-xl">Duo+ CCI Calculator</span>
          </template>

          <template #content>

              <div class="pb-16">

                  <calculator
                      :input-one="tableData.find(item => item.number === '0029')"
                      :input-two="tableData.find(item => item.number === '0030')"
                  ></calculator>

              </div>
          </template>

      </slide-over-panel>


    <!-- The picker sits in its own card above the table: the AjaxTable #search slot is a single
         centred flex cell sized for one input, and it crushes a panel this tall. -->
    <div v-if="isMultiple" class="mb-4">
      <!-- Loads its own tree for this site; the serials it carries feed the device count below. -->
      <point-select-tree v-model="selectedMeterIds"
                         with-serial
                         @loaded="meterTree = $event">
      </point-select-tree>
      <p class="mt-2 text-sm text-gray-600">
        {{ selectedMeterIds.length }} {{ selectedMeterIds.length === 1 ? 'point' : 'points' }} selected
        &rarr;
        <span class="font-medium text-gray-900">{{ selectedDeviceCount }}</span>
        {{ selectedDeviceCount === 1 ? 'device' : 'devices' }} will receive the packet
        <span v-if="pointsWithoutDevice > 0" class="text-yellow-700">
          &mdash; {{ pointsWithoutDevice }} selected {{ pointsWithoutDevice === 1 ? 'point has' : 'points have' }} no device and will be skipped
        </span>
      </p>
    </div>

    <ajax-table v-model:loading="ajaxTable.loading"
                v-model:search="ajaxTable.search"
                v-model:sort-by="ajaxTable.sortBy"
                v-model:sort-desc="ajaxTable.sortDesc"
                v-model:page="ajaxTable.page"
                v-model:items-per-page="ajaxTable.itemsPerPage"
                v-model:page-info="ajaxTable.pageInfo"
                :data="tableData"
                :headers="tableHeaders"
                :exportable="false"
                @loadData="loadData"
    >

      <template #search
                class="self-start flex items-center w-full md:w-auto ml:0 md:ml-2">
        <div v-if="!isMultiple" class="w-full">
          <select2 class=""
                   name="serial_number"
                   :closeOnSelect="true"
                   :options='serialNumbers'
                   v-model:value="serial"
                   placeholder="Select A Serial Number">
          </select2>
        </div>
        <!-- The slot has fallback content, so leaving it empty would render AjaxTable's own
             search box — a dead control here, since the tree payload ignores it. -->
        <span v-else></span>
      </template>

      <template #above_table_center
                class="self-start flex items-center w-full md:w-auto ml:0 md:ml-2">
        <div class="w-full">
          <div class="inline w-1/5">
            <span class="inline-flex align-middle rounded-md border-2 border-blue-200 overflow-hidden">
              <button v-for="option in modeOptions"
                      :key="option.value"
                      class="px-4 py-1 text-sm leading-5 font-sm font-sans focus:outline-none"
                      :class="[mode === option.value ? 'bg-blue-200 text-blue-900' : 'bg-white-600 text-blue-700 hover:bg-blue-50']"
                      @click="mode = option.value">
                {{ option.label }}
              </button>
            </span>
          </div>
          <div class="inline w-1/5">
            <button class="items-center ml-2 px-12 py-1 border-2 border-blue-200 text-sm leading-5 font-sm font-sans rounded-md text-blue-700 bg-white-600 focus:outline-none focus:ring-blue-200"
                    @click="loadData">
              Refresh
            </button>
          </div>
          <div v-if="!isMultiple" class="inline w-1/5">
            <button class="items-center ml-2 px-8 py-1 border-2 text-sm leading-5 font-sm font-sans rounded-md focus:outline-none"
                    :class="[(clearingPackets || !serial || !canManageParameters) ? 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed' : 'border-red-200 text-red-700 bg-white-600 hover:bg-red-50 focus:ring-red-200']"
                    :disabled="clearingPackets || !serial || !canManageParameters"
                    @click="clearAllPackets">
              {{ clearingPackets ? 'Clearing...' : 'Clear All Packets' }}
            </button>
          </div>
        </div>
      </template>

      <template #items-per-page>
<!--        We leave this blank because we don't have a good way to do items per page...-->
        <span></span>
      </template>

      <!-- Parameter Name -->
      <template #col-1="tableProps">
          <div class="flex justify-between items-center w-full">

              {{ tableProps.row.name }}
              <!-- Tooltip Part of this Does not Work, Possible Addition Later? -->
              <span v-show="tableProps.row.packetQueued"
                    class="tippy"
                    :data-tippy-content="'Packet Queued: ' + tableProps.row.packetCreated">
                  <span class="lnr icon-self-timer text-yellow-500 text-xs pl-4"></span>
              </span>

              <!-- Device rejected this parameter (ERR[1-6]); shown distinct from a queued packet -->
              <span v-show="tableProps.row.hasError"
                    :data-tippy-content="'Device rejected parameter: ' + tableProps.row.error"
                    class="tippy">
                  <span class="lnr icon-warning text-red-500 text-xs pl-4"></span>
              </span>


              <div v-if="['0029', '0030'].includes(String(tableProps.row.number))">
                  <button  class="w-full items-center ml-2 px-8 py-2 border-2 border-blue-200 text-sm leading-5 font-sm font-sans rounded-md text-blue-700 cursor-pointer bg-white-600 hover:bg-blue-200 active:bg-blue-300 focus:outline-none focus:shadow-outline-blue focus:border-blue-400"
                           @click="updateShow(true)"
                  >
                      Duo+ CCI Calculator
                  </button>
              </div>
          </div>

      </template>

      <!-- Parameter Value -->
      <template #col-2="tableProps">
        <input type="text"  class="w-1/2 items-center ml-2 px-4 py-2 border-2 border-blue-200 text-sm leading-5 font-sm font-sans rounded-md text-blue-700 bg-white-600 focus:outline-none focus:ring-blue-200"
               :name="tableProps.row.number"
               v-model="tableProps.row.value"
               placeholder="-">
      </template>

      <!-- Update Time -->
      <template #col-3="tableProps">
        {{ tableProps.row.update_time }}
      </template>

      <!-- Query Column -->
      <template #col-4="tableProps">
        <button class="w-full items-center ml-2 px-8 py-2 border-2 border-blue-200 text-sm leading-5 font-sm font-sans rounded-md text-blue-700"
                :class="[(tableProps.row.querying) ? standbyClass :
                                        ((tableProps.row.setting || !shouldProcessParameter(tableProps.row)) ? disabledClass : normalClass),
                                        ]"
                @click="queryParameter(tableProps.row)">
          {{ (tableProps.row.querying) ? "Querying" : tableProps.row.query }}
        </button>
      </template>

      <!-- Set Column -->
      <template #col-5="tableProps">
        <button  class="w-full items-center ml-2 px-8 py-2 border-2 border-blue-200 text-sm leading-5 font-sm font-sans rounded-md text-blue-700"
                 :class="[(tableProps.row.setting) ? standbyClass :
                                        ((tableProps.row.querying || !shouldProcessParameter(tableProps.row)) ? disabledClass : normalClass),]"
                 @click="setParameter(tableProps.row)">
          {{ (tableProps.row.setting) ? "Setting" : tableProps.row.set }}
        </button>
      </template>
    </ajax-table>
  </div>
</template>

<script>
import axios from "axios"
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import {ajaxTableMixin} from "@/mixins/ajaxTableMixin";
import AjaxTable from "@/components/elements/AjaxTable";
import Select2 from "@/components/elements/select2"
import SonicuHeader from "@/components/elements/SonicuHeader.vue";
import SlideOverPanel from "@/components/elements/SlideOverPanel.vue";
import Toggle from "@/components/elements/toggle.vue";
import Channel from "@/components/DuoPlusCCICalculator/Channel.vue";
import Calculator from "@/components/DuoPlusCCICalculator/Calculator.vue";
import BannerSuccess from "@/components/Banners/BannerSuccess.vue";

export default {
  name: "ConfigParametersIndex",
  mixins: [SonicuCoreMixin, iconNamesMixin, ajaxTableMixin,],
  components: {BannerSuccess, Calculator, Channel, Toggle, SlideOverPanel, SonicuHeader, AjaxTable, Select2, },
  props: {
    serialNumbers: Object,
  },
  data() {
    return {
      tableHeaders: [
        {
          name: 'Parameter Name',
          key: 'name',
          sortBy: '',
        },
        {
          name: 'Parameter Value',
          key: 'type',
          sortBy: '',
        },
        {
          name: 'Update Time',
          key: 'meters',
          sortBy: '',
        },
        {
          name: 'Query',
          key: 'query',
          sortBy: '',
        },
        {
          name: 'Set',
          key: 'set',
          sortBy: '',
        }
      ],
      tableData: [],
      serial: null,
      // Single talks to one device via the serial select2; multiple fans a parameter out across
      // every device behind the selected points.
      mode: 'single',
      modeOptions: [
        {value: 'single', label: 'Single device'},
        {value: 'multiple', label: 'Multiple devices'},
      ],
      meterTree: [],
      selectedMeterIds: [],
      treeLoaded: false,
      clearingPackets: false,
      showSuccessBanner: false,
      successMessage: '',

      // Editing Modal
      requestNumber: 0,
      normalClass: 'cursor-pointer bg-white-600 hover:bg-blue-200 active:bg-blue-300 focus:outline-none focus:shadow-outline-blue focus:border-blue-400',
      standbyClass: 'cursor-not-allowed bg-yellow-100 hover:bg-yellow-100 focus:bg-yellow-100 active:bg-yellow-100',
      disabledClass: 'cursor-not-allowed bg-white-600 hover:bg-white-600 focus:bg-white-600 active:bg-white-600',
      _show: this.show,
    }
  },
  created() {
  },
  mounted() {

  },
  methods: {
    loadData() {
      if (this.isMultiple) {
        this.loadTree()
        return
      }
      if (this.serial === null) {
        // Nothing to fetch yet. Leaving loading true parks the table on a permanent spinner.
        this.ajaxTable.loading = false
        return;
      }
      this.ajaxTable.loading = true
      this.ajaxTable.requestNumber++
      let params = JSON.parse(JSON.stringify(this.ajaxTable))
      params.requestNumber = this.ajaxTable.requestNumber
      axios.get(this.company.buildCompanyRoute('device/config-parameters/' + this.serial), {
        params: params,
      }).then(response => {
        this.tableData = this.decorateRows(response.data)

        this.ajaxUpdateTableData(this.tableData)
      }).catch(error => {
        console.log(error)
      }).then(() => {
        this.ajaxTable.loading = false
      })
    },
    // The device-less parameter rows for multiple mode, fetched once. The point tree loads itself
    // inside PointSelectTree, which remounts (and fetches its tree again) each time multiple mode opens.
    loadTree() {
      if (this.treeLoaded) {
        this.ajaxTable.loading = false
        return
      }
      this.ajaxTable.loading = true
      axios.get(this.company.buildCompanyRoute('device/config-parameters/tree')).then(response => {
        this.tableData = this.decorateRows(response.data.parameters)
        this.treeLoaded = true

        this.ajaxUpdateTableData(this.tableData)
      }).catch(error => {
        console.log(error)
      }).then(() => {
        this.ajaxTable.loading = false
      })
    },
    decorateRows(rows) {
      rows.forEach(row => {
        row.querying = false
        row.setting = false
      })
      return rows
    },
    queryParameter(parameter) {
      this.sendParameter(parameter, '', 'querying')
    },
    setParameter(parameter) {
      this.sendParameter(parameter, parameter.value, 'setting')
    },
    sendParameter(parameter, value, flag) {
      if (!this.shouldProcessParameter(parameter)) {
        return
      }
      // An empty value is a Query to the device, so a mis-clicked Set would quietly fan a Query
      // out across the whole selection. Single mode keeps its existing behaviour.
      if (this.isMultiple && flag === 'setting' && String(value ?? '').trim() === '') {
        return
      }
      parameter[flag] = true
      parameter.packetQueued = true

      if (this.isMultiple) {
        this.sendParameterToSelection(parameter, value, flag)
        return
      }

      axios.post(this.company.buildCompanyRoute('device/config-parameters'), {
        number: parameter.number,
        value: value,
        serial: parameter.serial,
      }).then(
        response => {
        }
      ).catch(
        error => {
        }
      )
    },
    sendParameterToSelection(parameter, value, flag) {
      axios.post(this.company.buildCompanyRoute('device/config-parameters/bulk'), {
        number: parameter.number,
        value: value,
        meter_ids: this.selectedMeterIds,
      }).then(response => {
        this.successMessage = this.summaryMessage(parameter, value, response.data.summary)
        this.showSuccessBanner = true
      }).catch(error => {
        console.error('Error queueing parameter:', error)
        alert('Error queueing parameter')
      }).then(() => {
        // Unlatch, so the same parameter can be re-sent to a different selection without a refresh.
        parameter[flag] = false
        parameter.packetQueued = false
      })
    },
    summaryMessage(parameter, value, summary) {
      const action = value === '' ? 'Queried' : `Set to ${value}`
      let message = `${action} — ${parameter.name}: queued on ${summary.queued} ${summary.queued === 1 ? 'device' : 'devices'}`
      if (summary.skipped > 0) {
        message += `, ${summary.skipped} skipped`
      }
      if (summary.failed > 0) {
        message += `, ${summary.failed} failed`
      }
      return message
    },
    shouldProcessParameter(parameter) {
      if (!this.canManageParameters) {
        return false
      }
      if (this.isMultiple) {
        return this.selectedMeterIds.length > 0 && !parameter.querying && !parameter.setting
      }
      if (parameter.packetQueued) {
        return false
      }
      return !parameter.querying && !parameter.setting
    },
      updateShow(show) {
          this.$data._show = show
          this.$emit('update:show', this.$data._show)
          this.advanced_settings = false;
      },
      clearAllPackets() {
        if (!this.serial || !this.canManageParameters || this.clearingPackets) {
          return;
        }
        
        this.clearingPackets = true;
        
        axios.delete(this.company.buildCompanyRoute('device/config-parameters/' + this.serial))
          .then(response => {
            if (response.data.success) {
              const { deleted_count, message } = response.data;
              this.successMessage = `${message} (${deleted_count} packets cleared)`;
              this.showSuccessBanner = true;
              this.loadData();
            } else {
              console.error('Failed to clear packets');
              alert('Failed to clear packets');
            }
          })
          .catch(error => {
            console.error('Error clearing packets:', error);
            alert('Error clearing packets');
          })
          .finally(() => {
            this.clearingPackets = false;
          });
      },
  },
  computed: {
    canManageParameters() {
      return this.permissions.can('manage_config_parameters')
    },
    isMultiple() {
      return this.mode === 'multiple'
    },
    // PointSelectTree only renders the point name, so the serial it carries is read here instead.
    meterSerials() {
      const serials = {}
      this.meterTree.forEach(area => area.zones.forEach(zone => zone.meters.forEach(meter => {
        serials[meter.id] = meter.serial
      })))
      return serials
    },
    // Several points can share one radio, and command 198 addresses the radio — so this is the
    // number of packets the send actually queues, not the number of points ticked.
    selectedDeviceCount() {
      return new Set(this.selectedMeterIds.map(id => this.meterSerials[id]).filter(Boolean)).size
    },
    pointsWithoutDevice() {
      return this.selectedMeterIds.filter(id => !this.meterSerials[id]).length
    },
  },
  watch: {
      serial() {
          if (this.isMultiple) {
              return
          }
          this.loadData()
      },
      // AjaxTable only re-emits loadData on page/sort/search/itemsPerPage/mount, so the mode
      // switch has to ask for its own reload.
      mode() {
          if (!this.isMultiple && this.serial === null) {
              this.tableData = []
              this.ajaxUpdateTableData(this.tableData)
          }
          this.loadData()
      },
  },
}
</script>

<style scoped>

</style>